/* KALKULAČKY /calculators — SLOVENŠTINA, EURA A ZEMĚ TENANTA (18. 10. 2026)
 * ============================================================================
 *
 * Majitel 18. 10. 2026: „slovensky, eura, vše ať sedí!! velmi důležitý“.
 *
 * 1) POPISKY (nález K-SK-01). Slovník `c.N.i / o / ds` v sk.js je ze STARÉ
 *    specifikace (před dubnem 2026) a je klíčovaný POŘADÍM. Konfigurace se
 *    přepsala, slovník ne, takže nad číslem stál popisek jiného pole
 *    („Horizont 7,0 %“, „Vek 1 700 €“, „Splatnosť 0 €“). Tady je překlad
 *    klíčovaný ČESKÝM TEXTEM z konfigurace: stejný text = stejný význam,
 *    pořadí ani přidané pole nic neposune. Chybějící překlad hlídá
 *    __tests__/calculatorPopiskySedi.test.js (SK větev).
 *
 * 2) EURA (nález K-SK-02). Výchozí hodnoty a rozsahy jezdců byly v korunových
 *    řádech i pro tenanta v eurech (úvěr 3 000 000 €). `ROZSAHY_EUR` je
 *    rozsah podle MĚNY TENANTA (ne jazyka) — přibližně ÷ 25 a zaokrouhleno,
 *    stejně jako lib/nastroje/rozsahyJezdcu.js. Není to přepočet kurzem,
 *    jen rozumná výchozí poloha jezdce; poradce vždy zadá skutečná čísla.
 *
 * 3) ČESKÝ ZÁKON (nález K-SK-02). Kalkulačky, které počítají podle českých
 *    zákonných konstant (státní příspěvek DPS, stavebko 5 % / 1 000 Kč, DIP,
 *    daň 15/23 % a odpočty, limity ČNB, české dávky), se pro tenanta mimo ČR
 *    NEPOČÍTAJÍ — klient by dostal „státní příspěvek 340 € měsíčně“.
 *    Ověřená SK pravidla jsou jiná (a appka je zatím nemá jako engine):
 *      · III. pilier (DDS): bez státního příspěvku, jen odpočet max 180 €/rok
 *        (podpora.financnasprava.sk, článek 609555),
 *      · štátna prémia stavebného sporenia 2026: 6 %, max 70 €/rok
 *        (opatrenie MF SR č. 238/2025 Z. z.),
 *      · DPFO 2026: 19 / 25 / 30 / 35 % (podpora.financnasprava.sk 081400),
 *      · LTV: NBS 80 % (90 % jen v kvótě banky) — lib/nastroje/uverLimity.js,
 *      · DIP v SR neexistuje.
 *    SK verze těchto kalkulaček = rozhodnutí na ráno (viz docs/ZADANI-CUNG.md).
 */

/** Kalkulačky s českým zákonem — mimo ČR zamčené s poctivou větou. */
export const JEN_CZ = Object.freeze(["pension", "buildingSavings", "dip", "taxSavings", "retirementGap", "lifeInsurance", "ltv"]);

/** Platí kalkulačka pro zemi tenanta? */
export function platiProZemi(idKalkulacky, zeme) {
  return String(zeme || "CZ").toUpperCase() === "CZ" || !JEN_CZ.includes(idKalkulacky);
}

/* Samostatné nástroje jen pro ČR (19. 10. 2026, K-SK-03 a K-SK-11):
 *   · /penze  — české penzijko DPS a DIP v Kč (SK verze III. piliera chybí,
 *               rozhodnutí 6: dlaždici u SK skrýt),
 *   · /fixace — historie sazeb ČNB 2006–2026 a doporučení ČNB k DSTI.
 * Mimo ČR se dlaždice ani záložka skupiny neukáže. */
export const JEN_CZ_NASTROJE = Object.freeze(["penze", "fixace"]);
const JEN_CZ_URL = Object.freeze(["/penze", "/fixace"]);

/** Platí odkaz (dlaždice, záložka skupiny) pro zemi tenanta? */
export function platiUrlProZemi(url, zeme) {
  if (String(zeme || "CZ").toUpperCase() === "CZ" || !url) return true;
  const u = String(url);
  if (JEN_CZ_URL.some((cesta) => u === cesta || u.startsWith(`${cesta}?`) || u.startsWith(`${cesta}/`))) return false;
  const calc = /^\/calculators\?calc=([A-Za-z]+)/.exec(u);
  return !(calc && JEN_CZ.includes(calc[1]));
}

/* Rozsahy peněžních jezdců pro tenanta v EURECH (id → klíč pole → rozsah).
 * Jen měnově neutrální kalkulačky; JEN_CZ se mimo ČR nepočítají. */
export const ROZSAHY_EUR = Object.freeze({
  mortgage: {
    amount: { min: 20000, max: 1200000, default: 120000, step: 5000 },
    propertyValue: { min: 20000, max: 1200000, default: 160000, step: 5000 },
  },
  refinance: {
    balance: { min: 5000, max: 400000, default: 100000, step: 5000 },
    transferCost: { min: 0, max: 4000, default: 200, step: 50 },
  },
  earlyRepay: {
    amount: { min: 20000, max: 1200000, default: 120000, step: 5000 },
    extraPayment: { min: 2000, max: 200000, default: 8000, step: 1000 },
  },
  rentVsBuy: {
    propertyPrice: { min: 40000, max: 1200000, default: 200000, step: 5000 },
    rent: { min: 200, max: 2500, default: 700, step: 10 },
    downPayment: { min: 0, max: 400000, default: 40000, step: 2000 },
  },
  regularInvest: {
    monthly: { min: 20, max: 8000, default: 120, step: 10 },
  },
  compound: {
    initial: { min: 0, max: 2000000, default: 4000, step: 500 },
    monthly: { min: 0, max: 8000, default: 0, step: 10 },
  },
  dca: {
    amount: { min: 2000, max: 400000, default: 24000, step: 1000 },
  },
  inflation: {
    amount: { min: 500, max: 2000000, default: 40000, step: 500 },
  },
  fire: {
    expenses: { min: 400, max: 20000, default: 2000, step: 50 },
    current: { min: 0, max: 2000000, default: 8000, step: 500 },
    monthly: { min: 50, max: 8000, default: 600, step: 50 },
  },
  goalSavings: {
    goal: { min: 2000, max: 2000000, default: 40000, step: 1000 },
  },
  cashflow: {
    income1: { min: 0, max: 8000, default: 1800, step: 50 },
    income2: { min: 0, max: 8000, default: 0, step: 50 },
    housing: { min: 0, max: 3200, default: 700, step: 10 },
    food: { min: 0, max: 1200, default: 320, step: 10 },
    transport: { min: 0, max: 1200, default: 160, step: 10 },
    insurance: { min: 0, max: 800, default: 100, step: 5 },
    leisure: { min: 0, max: 1200, default: 200, step: 10 },
    kids: { min: 0, max: 2000, default: 0, step: 10 },
  },
  netWorth: {
    property: { min: 0, max: 4000000, default: 160000, step: 5000 },
    savings: { min: 0, max: 400000, default: 8000, step: 500 },
    investments: { min: 0, max: 2000000, default: 20000, step: 1000 },
    dps: { min: 0, max: 400000, default: 4000, step: 500 },
    mortgage: { min: 0, max: 1200000, default: 100000, step: 5000 },
    loans: { min: 0, max: 200000, default: 0, step: 500 },
  },
  rpsn: {
    amount: { min: 500, max: 200000, default: 8000, step: 500 },
    feeOrigination: { min: 0, max: 2000, default: 0, step: 20 },
    feeMonthly: { min: 0, max: 40, default: 0, step: 1 },
    insurance: { min: 0, max: 200, default: 0, step: 2 },
  },
});

/** Konfigurace kalkulačky pro měnu tenanta. CZK (i neznámá) = beze změny. */
export function konfiguraceProMenu(config, menaKod) {
  if (String(menaKod || "").toUpperCase() !== "EUR") return config;
  const rozsahy = ROZSAHY_EUR[config.id];
  if (!rozsahy) return config;
  return { ...config, inputs: config.inputs.map((inp) => (rozsahy[inp.key] ? { ...inp, ...rozsahy[inp.key] } : inp)) };
}

/* Český text z konfigurace → slovenský. Popisky polí, výsledků, řad a os
 * grafu, volby selectů a textové hodnoty výsledků. */
export const SK_TEXTY = Object.freeze({
  // — Hypotéka, refinancování, doplacení, LTV, nájem vs koupě —
  "Výše úvěru": "Výška úveru",
  "Úroková sazba (p.a.)": "Úroková sadzba (p. a.)",
  "Délka splatnosti": "Doba splatnosti",
  "Hodnota nemovitosti": "Hodnota nehnuteľnosti",
  "Měsíční splátka": "Mesačná splátka",
  "Celkem zaplaceno": "Celkom zaplatené",
  "Přeplatek": "Preplatok",
  "LTV": "LTV",
  "Jistina": "Istina",
  "Úroky": "Úroky",
  "Zbývající jistina": "Zostávajúca istina",
  "Stávající sazba": "Súčasná sadzba",
  "Nová sazba": "Nová sadzba",
  "Zbývající splatnost": "Zostávajúca splatnosť",
  "Náklady na převod": "Náklady na prevod",
  "Měsíční úspora": "Mesačná úspora",
  "Celková úspora": "Celková úspora",
  "Stará splátka": "Stará splátka",
  "Nová splátka": "Nová splátka",
  "Návratnost": "Návratnosť",
  "Měsíčně": "Mesačne",
  "Ročně": "Ročne",
  "Za celou dobu": "Za celú dobu",
  "Stará": "Stará",
  "Nová": "Nová",
  "Úroková sazba": "Úroková sadzba",
  "Doba splácení": "Doba splácania",
  "Mimořádná splátka": "Mimoriadna splátka",
  "Vložit po (rok od začátku)": "Vložiť po (rok od začiatku)",
  "Úspora na úrocích": "Úspora na úrokoch",
  "Nová měs. splátka": "Nová mes. splátka",
  "Zůstatek po vložení": "Zostatok po vklade",
  "Zkrácení doby (alt.)": "Skrátenie doby (alt.)",
  "Úroky původně": "Úroky pôvodne",
  "Úroky po splátce": "Úroky po splátke",
  "Věk žadatele": "Vek žiadateľa",
  "Účel": "Účel",
  "Vlastní bydlení": "Vlastné bývanie",
  "Investiční": "Investičná",
  "Závazný limit ČNB": "Záväzný limit ČNB",
  "Doporučení ČNB (investiční)": "Odporúčanie ČNB (investičná)",
  "Splňuje limit": "Spĺňa limit",
  "Splňuje doporučení": "Spĺňa odporúčanie",
  "ANO ✓": "ÁNO ✓",
  "NE ✗": "NIE ✗",
  "Vlastní zdroje (potřeba)": "Vlastné zdroje (potreba)",
  "Vlastní zdroje (zadáno)": "Vlastné zdroje (zadané)",
  "Úvěr (LTV)": "Úver (LTV)",
  "Vlastní zdroje": "Vlastné zdroje",
  "Cena nemovitosti": "Cena nehnuteľnosti",
  "Měsíční nájem": "Mesačný nájom",
  "Akontace": "Vlastné zdroje (akontácia)",
  "Úrok hypotéky": "Úrok hypotéky",
  "Horizont srovnání": "Horizont porovnania",
  "Výnos investice": "Výnos investície",
  "Vyhrává: KOUPĚ": "Vyhráva: KÚPA",
  "Vyhrává: NÁJEM": "Vyhráva: NÁJOM",
  "Náklady koupě": "Náklady kúpy",
  "Náklady nájem": "Náklady nájmu",
  "Majetek (koupě)": "Majetok (kúpa)",
  "Investovaná akontace (nájem)": "Investovaná akontácia (nájom)",
  // — Investice —
  "Měsíční vklad": "Mesačný vklad",
  "Doba investice": "Doba investície",
  "Očekávaný výnos": "Očakávaný výnos",
  "Manažerský poplatek": "Manažérsky poplatok",
  "Hodnota portfolia": "Hodnota portfólia",
  "Celkem vloženo": "Celkom vložené",
  "Čistý výnos": "Čistý výnos",
  "Násobek vkladů": "Násobok vkladov",
  "Vlastní vklady": "Vlastné vklady",
  "Počáteční vklad": "Počiatočný vklad",
  "Výnos": "Výnos",
  "Doba": "Doba",
  "Konečná hodnota": "Konečná hodnota",
  "Výnos celkem": "Výnos celkom",
  "Pravidlo 72": "Pravidlo 72",
  "nikdy": "nikdy",
  "Hodnota": "Hodnota",
  "Vklady": "Vklady",
  "Celková částka": "Celková suma",
  "DCA (měsíců)": "DCA (mesiacov)",
  "Vyhrává: LUMP SUM": "Vyhráva: JEDNORAZOVO",
  "Vyhrává: DCA": "Vyhráva: DCA",
  "Lump Sum": "Jednorazovo",
  "DCA": "DCA",
  "Rozdíl": "Rozdiel",
  "Částka": "Suma",
  "Inflace": "Inflácia",
  "Horizont": "Horizont",
  "Ztráta kupní síly": "Strata kúpnej sily",
  "Ztráta v %": "Strata v %",
  "Budoucí cena dnešních": "Budúca cena dnešnej sumy",
  "Reálná kupní síla": "Reálna kúpna sila",
  "Měsíční výdaje": "Mesačné výdavky",
  "Aktuální majetek": "Súčasný majetok",
  "Měsíční investice": "Mesačná investícia",
  "Bezpečná míra čerpání": "Bezpečná miera čerpania",
  "Cílová částka FIRE": "Cieľová suma FIRE",
  "Roky do FIRE": "Roky do FIRE",
  "Měsíčně potřeba": "Mesačne treba",
  "Roční čerpání": "Ročné čerpanie",
  "Růst portfolia": "Rast portfólia",
  "Cíl FIRE": "Cieľ FIRE",
  "Cílová částka": "Cieľová suma",
  "Doba spoření": "Doba sporenia",
  "Měsíčně potřeba ukládat": "Mesačne treba odkladať",
  "Doporučení": "Odporúčanie",
  "Spořicí účet / dluhopisy": "Sporiaci účet / dlhopisy",
  "Smíšené fondy": "Zmiešané fondy",
  "Akciové ETF": "Akciové ETF",
  // — Penze, pojištění, stavebko, DIP (český zákon; SK jazyk u CZ tenanta) —
  "Příspěvek zaměstnavatele": "Príspevok zamestnávateľa",
  "Strategie": "Stratégia",
  "Konzervativní (3 %)": "Konzervatívna (3 %)",
  "Vyvážená (5 %)": "Vyvážená (5 %)",
  "Dynamická (7 %)": "Dynamická (7 %)",
  "Hodnota v důchodu": "Hodnota v dôchodku",
  "Měs. státní příspěvek": "Mes. štátny príspevok",
  "Roční daňová úspora": "Ročná daňová úspora",
  "Vlastní vklady celkem": "Vlastné vklady celkom",
  "Bez st. příspěvku": "Bez št. príspevku",
  "Se st. příspěvkem": "So št. príspevkom",
  "Aktuální hrubá mzda": "Súčasná hrubá mzda",
  "Aktuální věk": "Súčasný vek",
  "Věk důchodu": "Vek odchodu do dôchodku",
  "Růst mzdy": "Rast mzdy",
  "Aktuální DPS": "Súčasné DPS",
  "Měs. vklad DPS": "Mes. vklad DPS",
  "Měsíční mezera": "Mesačná medzera",
  "Státní důchod (odhad)": "Štátny dôchodok (odhad)",
  "Z DPS / měsíc": "Z DPS / mesiac",
  "Stále chybí": "Stále chýba",
  "Státní důchod": "Štátny dôchodok",
  "Z DPS": "Z DPS",
  "Měsíční čistý příjem": "Mesačný čistý príjem",
  "Počet dětí": "Počet detí",
  "Měs. splátka hypotéky": "Mes. splátka hypotéky",
  "Zbývá splatit hypotéku": "Zostáva splatiť hypotéku",
  "Povolání": "Povolanie",
  "Kancelář": "Kancelária",
  "Manuální": "Manuálna práca",
  "Riziková práce": "Riziková práca",
  "Smrt": "Smrť",
  "Invalidita 3. stupně": "Invalidita 3. stupňa",
  "Invalidita 1.+2.": "Invalidita 1. + 2.",
  "Vážné nemoci": "Vážne choroby",
  "PN denní dávka": "PN denná dávka",
  "Invalidita 3.": "Invalidita 3.",
  "Invalidita 1+2": "Invalidita 1 + 2",
  "Doporučené krytí": "Odporúčané krytie",
  "Garantovaný úrok": "Garantovaný úrok",
  "Roční poplatek": "Ročný poplatok",
  "Naspořeno celkem": "Nasporené celkom",
  "Státní podpora": "Štátna podpora",
  "Úroky (po dani)": "Úroky (po dani)",
  "Sazba daně": "Sadzba dane",
  "15 % (základní)": "15 % (základná)",
  "23 % (vyšší pásmo)": "23 % (vyššie pásmo)",
  "Daň. úspora celkem": "Daň. úspora celkom",
  "Portfolio": "Portfólio",
  // — Osobní finance —
  "Čistý příjem 1": "Čistý príjem 1",
  "Čistý příjem 2 (partner)": "Čistý príjem 2 (partner)",
  "Bydlení": "Bývanie",
  "Jídlo": "Jedlo",
  "Doprava": "Doprava",
  "Pojištění": "Poistenie",
  "Volný čas": "Voľný čas",
  "Děti": "Deti",
  "Měsíční rozdíl": "Mesačný rozdiel",
  "Míra úspor": "Miera úspor",
  "Příjmy": "Príjmy",
  "Výdaje": "Výdavky",
  "Volné": "Voľné",
  "Nemovitosti": "Nehnuteľnosti",
  "Úspory + spořicí účty": "Úspory + sporiace účty",
  "Investice (ETF, fondy)": "Investície (ETF, fondy)",
  "DPS + DIP": "Dôchodkové sporenie",
  "Hypotéky": "Hypotéky",
  "Spotřebitelské úvěry": "Spotrebiteľské úvery",
  "Net Worth": "Čistá hodnota majetku",
  "Aktiva celkem": "Aktíva celkom",
  "Pasiva celkem": "Pasíva celkom",
  "Likvidní podíl": "Likvidný podiel",
  "Zadluženost": "Zadlženosť",
  "Úspory": "Úspory",
  "Investice": "Investície",
  "DPS+DIP": "Dôchodkové sporenie",
  "Pasiva": "Pasíva",
  "Úroky z hypotéky (ročně)": "Úroky z hypotéky (ročne)",
  "Vklad DPS (ročně, jen NAD 20 400)": "Vklad DPS (ročne, len NAD 20 400)",
  "Vklad DIP (ročně)": "Vklad DIP (ročne)",
  "Vklad IŽP (ročně)": "Vklad IŽP (ročne)",
  "Hypotéka před 2021?": "Hypotéka pred 2021?",
  "Ne (limit 150 000)": "Nie (limit 150 000)",
  "Ano (limit 300 000)": "Áno (limit 300 000)",
  "Roční úspora na dani": "Ročná úspora na dani",
  "Celkový odpočet": "Celkový odpočet",
  "Z hypotéky": "Z hypotéky",
  "Z DPS+DIP+ŽP": "Z DPS + DIP + ŽP",
  "Hypotéka": "Hypotéka",
  "DPS+DIP+ŽP": "DPS + DIP + ŽP",
  "Úrok": "Úrok",
  "Doba úvěru": "Doba úveru",
  "Poplatek za sjednání": "Poplatok za uzatvorenie",
  "Poplatek za vedení (měs.)": "Poplatok za vedenie (mes.)",
  "Pojištění splátek (měs.)": "Poistenie splátok (mes.)",
  "RPSN": "RPMN",
  "Měsíční náklad": "Mesačný náklad",
  "%": "%",
  // — Bublina u pole —
  "Celková částka úvěru od banky": "Celková suma úveru od banky",
});

/* Texty s číslem uvnitř (popisek nebo hodnota se skládá za běhu). */
const VZORY = [
  [/^Reálná hodnota za (\d+) let$/, (_, n) => `Reálna hodnota za ${n} r.`],
  [/^Rozdíl (.+)$/, (_, x) => `Rozdiel ${x}`],
  [/^\+(\d+) let$/, (_, n) => `+${n} r.`],
  [/^(\d+)\. rok$/, (_, n) => `${n}. rok`],
  [/^\d+$/, (x) => x],
];

/** Slovenský text, nebo `null`, když překlad chybí (volající rozhodne). */
export function skText(cz) {
  if (typeof cz !== "string") return null;
  if (Object.prototype.hasOwnProperty.call(SK_TEXTY, cz)) return SK_TEXTY[cz];
  for (const [re, fn] of VZORY) {
    const m = cz.match(re);
    if (m) return fn(...m);
  }
  return null;
}

/* Popis kalkulačky slovensky — přeložený z AKTUÁLNÍ konfigurace (starý `c.N.d`
 * patří ke staré specifikaci). Věty o českém zákoně jsou vypuštěné. */
export const SK_POPISY = Object.freeze({
  mortgage: "Hypotéka je dlhodobý účelový úver na bývanie so záložným právom k nehnuteľnosti. Klient spláca mesačnú anuitu (úrok + istina).",
  refinance: "Splatenie starej hypotéky novým úverom za lepších podmienok. Najvýhodnejší moment je koniec fixácie.",
  earlyRepay: "Mimoriadna splátka znižuje zostatok istiny → kratšia doba alebo nižšia splátka. Podmienky bez poplatku určuje zmluva a zákon v krajine úveru.",
  ltv: "LTV = úver / hodnota nehnuteľnosti × 100. Príklad: byt za 5 mil., hypotéka 4 mil. = LTV 80 %.",
  rentVsBuy: "Porovnanie: klient v nájme má flexibilitu, klient, ktorý kupuje, buduje majetok. Kľúčom je investovať akontáciu v prípade nájmu.",
  regularInvest: "DCA: klient mesačne investuje rovnakú sumu a využíva zložené úročenie.",
  compound: "Výnosy sa pripisujú k istine a ďalej sa úročia. Pravidlo 72: za koľko rokov sa peniaze zdvojnásobia = 72 / výnos.",
  dca: "Vklad naraz historicky vyhral v 73 % období (SBBI 1926–2026), DCA je ale psychologicky pohodlnejšie.",
  inflation: "Inflácia ukrajuje kúpnu silu peňazí. 2,5 % × 20 rokov = strata 39 % kúpnej sily.",
  fire: "Cieľ: nasporiť 25 × ročné výdavky. Pravidlo 4 %: z portfólia môžeš ročne čerpať 4 %.",
  goalSavings: "Klient má cieľ, dopočítame mesačný vklad. Odporúčanie triedy aktív podľa horizontu.",
  pension: "Doplnkové penzijné sporenie (DPS) podľa českého zákona: klient si pravidelne sporí, štát prispieva.",
  retirementGap: "Rozdiel medzi poslednou mzdou a štátnym dôchodkom (výpočet podľa ČR).",
  lifeInsurance: "Krytie: úmrtie, invalidita 1.–3. stupňa, vážne choroby, pracovná neschopnosť. 96 % invalidít je z choroby, nie z úrazu.",
  buildingSavings: "Stavebné sporenie so štátnou podporou podľa českého zákona. Viazacia doba 6 rokov.",
  dip: "DIP — dlhodobý investičný produkt podľa českého zákona (od 1. 1. 2024). Pravidlo 120/60: výber po 10 rokoch a v 60 rokoch veku.",
  cashflow: "Zdravie mesačného rozpočtu. Minimum: kladný rozdiel ≥ 10 % príjmu, ideál 20 %, pri 30 % sa ciele plnia veľmi rýchlo.",
  netWorth: "Čistá hodnota majetku je najdôležitejšie číslo finančného zdravia. Mala by každý rok rásť.",
  taxSavings: "Súhrn daňových odpočtov podľa českého zákona 2026.",
  rpsn: "RPMN = úrok + všetky poplatky. Vždy porovnávajte RPMN, nie len úrok.",
});

/** Slovo jednotky u čísla podle jazyka (fmt v CalculatorEngine). */
export function jednotkaSlovem(unit, lang) {
  const sk = lang === "sk";
  if (unit === "let") return sk ? "r." : "let";
  if (unit === "mesicu") return sk ? "mes." : "měsíců";
  return unit;
}

/* Poctivá věta u kalkulačky s českým zákonem pro tenanta mimo ČR.
 * Slovensky pro `sk`, jinak česky (tenant mimo ČR je dnes jen SK).
 * Bez zkratky ČNB (24. 10. 2026): slovenský tenant českou instituci nevidí. */
const JEN_CZ_TEXT = Object.freeze({
  cs: {
    stitek: "Jen pro ČR",
    detail: "Tahle kalkulačka počítá podle českého zákona (státní příspěvky, daně, úvěrové limity). Pro Slovensko platí jiná pravidla, proto ji zde nepočítáme — výsledek by klientovi neseděl.",
  },
  sk: {
    stitek: "Len pre ČR",
    detail: "Táto kalkulačka počíta podľa českého zákona (štátne príspevky, dane, úverové limity). Na Slovensku platia iné pravidlá, preto ju tu nepočítame — výsledok by klientovi nesedel.",
  },
});

export function textJenCz(lang) {
  return JEN_CZ_TEXT[lang] || JEN_CZ_TEXT.cs;
}
